import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

const LanguageSwitcherContainer = styled.div`
  position: relative;
  display: inline-block;
  z-index: 20; /* Garantir que o dropdown fique acima de outros elementos */
`;

const CurrentLanguage = styled.button`
  background-color: transparent;
  color: ${props => props.theme.colors.text};
  border: 1px solid ${props => props.theme.colors.primary};
  border-radius: 4px;
  padding: 0.5rem 1.5rem 0.5rem 0.8rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-width: 130px; /* Aumentado para acomodar melhor os nomes de idiomas */
  position: relative;
  transition: all 0.2s ease;
  font-weight: 500;
  white-space: nowrap; /* Evita quebra de linha no texto */

  &:after {
    content: "▼";
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 0.7rem;
    color: ${props => props.theme.colors.primary};
    transition: transform 0.2s ease;
  }

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.secondary};
    box-shadow: 0 0 0 2px ${props => props.theme.colors.shadow};
  }

  &:hover {
    background-color: rgba(0, 163, 255, 0.1);
  }

  &[aria-expanded="true"]:after {
    transform: translateY(-50%) rotate(180deg);
  }
`;

const LanguageDropdown = styled.ul<{ isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  list-style: none;
  padding: 0;
  margin: 4px 0 0 0;
  background-color: white;
  border: 1px solid ${props => props.theme.colors.primary};
  border-radius: 4px;
  z-index: 10;
  display: ${props => (props.isOpen ? "block" : "none")};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
`;

const LanguageOption = styled.li`
  padding: 0.75rem 1rem;
  cursor: pointer;
  color: black !important; /* Garantir que a cor seja sempre preta */
  font-weight: 500;
  display: flex;
  align-items: center;
  white-space: nowrap; /* Evita quebra de linha no texto */

  &:hover {
    background-color: #f0f0f0;
  }

  &.active {
    background-color: #e0e0e0;
    font-weight: 600;
    color: black !important;
    border-left: 3px solid ${props => props.theme.colors.primary};
  }

  &:not(:last-child) {
    border-bottom: 1px solid #f0f0f0;
  }
`;

// Style to visually hide but keep available for screen readers
const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
`;

const LanguageSwitcher = () => {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const languages = {
    en: "English",
    pt: "Português",
    es: "Español",
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Close dropdown on ESC key press
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscKey);
      return () => {
        document.removeEventListener("keydown", handleEscKey);
      };
    }
  }, [isOpen]);
  // Get accessibility text based on current language
  const getAccessibilityText = () => {
    return t("common:selectLanguage", "Select language");
  };
  const handleLanguageChange = (language: string) => {
    // Salvar o idioma explicitamente no localStorage antes de mudar
    localStorage.setItem("i18nextLng", language);

    // Se o idioma atual não for o mesmo que o selecionado
    if (i18n.language !== language) {
      i18n.changeLanguage(language).then(() => {
        // Forçar atualização da UI em caso de problemas
        console.log(`Idioma alterado para: ${language}`);

        // Em ambientes de desenvolvimento, loga informações de debug
        if (process.env.NODE_ENV !== "production") {
          console.log("i18n language:", i18n.language);
          console.log("localStorage:", localStorage.getItem("i18nextLng"));
        }
      });
    }

    setIsOpen(false);
  };
  return (
    <LanguageSwitcherContainer ref={dropdownRef}>
      <VisuallyHidden>
        <label id="language-label">{getAccessibilityText()}</label>
      </VisuallyHidden>

      <CurrentLanguage
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="listbox"
        aria-labelledby="language-label"
        aria-expanded={isOpen}
        title={getAccessibilityText()}
      >
        {languages[i18n.language as keyof typeof languages] || languages.en}
      </CurrentLanguage>

      <LanguageDropdown
        isOpen={isOpen}
        role="listbox"
        aria-labelledby="language-label"
        tabIndex={-1}
      >
        {Object.entries(languages).map(([code, name]) => (
          <LanguageOption
            key={code}
            onClick={() => handleLanguageChange(code)}
            className={i18n.language === code ? "active" : ""}
            role="option"
            aria-selected={i18n.language === code}
            tabIndex={isOpen ? 0 : -1}
          >
            {i18n.language === code && (
              <span style={{ marginRight: "6px", color: "#00A3FF" }}>✓</span>
            )}
            {name}
          </LanguageOption>
        ))}
      </LanguageDropdown>
    </LanguageSwitcherContainer>
  );
};

export default LanguageSwitcher;
